import { banzosDoPontoDaEscada, localParaMundo, mundoParaLocalDoObjeto, pontosDeAncoragem } from './ancoragem'
import { barraDoMosquetaoParaOs, lugarNaBarra, naBarra } from './fitaDeAncoragem'
import { contornosDaCorda } from './contornoCorda'
import { folgaDoTrecho, pontosDoTrecho } from './folgaCorda'
import type { CenaTabletop, CordaTabletop, ObjetoTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

/**
 * O traçado de uma corda para desenhar: a linha por onde ela passa de fato,
 * a partir do percurso (os pontos de ancoragem por onde ela passa).
 *
 *  - Entre a entrada e a saída de uma polia, a corda contorna a roldana pelo
 *    canal, do lado oposto ao das pernas, em vez de cortar reto.
 *  - Nos outros pontos do meio, a dobra é arredondada (uma corda não faz
 *    quina viva, nem na borda de um barranco).
 *  - Trecho com folga pende, como em `folgaCorda.ts`.
 *  - Nas pontas presas a um ponto de ancoragem ou de carga, a corda termina
 *    como se monta na prática (só desenho): numa estrutura (viatura, escada,
 *    árvore...), anel de fita em volta do ponto, mosquetão no anel e o nó oito
 *    da corda no mosquetão; na escada, um anel em cada banzo, unidos pelo
 *    mosquetão; numa peça que já tem olhal ou argola (ponto de
 *    ancoragem, placa, polia...) e numa carga (maca, trabalhador), mosquetão e
 *    nó oito; na fita por envolvimento, que já tem o mosquetão dela, só o nó.
 *    O talabarte e a fita não ganham nada (têm conector ou costura próprios),
 *    nem a ponta que já está numa peça de nó oito.
 *
 * Só geometria: não muda comprimento nem conta de força. TypeScript puro.
 */

type V = VetorTabletop

/** Raio da roldana das polias (o mesmo do desenho) e o fundo do canal, onde a corda assenta. */
export const RAIO_RODANA_POLIA_M = 0.035
export const FUNDO_DO_CANAL_POLIA_M = RAIO_RODANA_POLIA_M * 0.72
/** Raio com que a corda dobra num ponto do meio do percurso. */
export const RAIO_DOBRA_CORDA_M = 0.04
/** Do fim da corda ao nó: o comprimento da alça. */
export const ALCA_DO_NO_M = 0.1

/** As roldanas de cada polia: o par entrada/saída e o z da roldana, no referencial da peça. */
const RODANAS: Readonly<Partial<Record<ObjetoTabletop['tipo'], ReadonlyArray<{ entrada: string; saida: string; z: number }>>>> = {
  polia: [{ entrada: 'entrada', saida: 'saida', z: 0 }],
  poliaDupla: [
    { entrada: 'entrada-1', saida: 'saida-1', z: -0.022 },
    { entrada: 'entrada-2', saida: 'saida-2', z: 0.022 },
  ],
}

/** Como a ponta da corda se prende ao ponto. */
/** `v-de-aneis`: na escada, um anel de fita em cada banzo, unidos pelo mosquetão. */
export type ConexaoDaPonta = 'anel-e-mosquetao' | 'v-de-aneis' | 'mosquetao' | 'no'

/** Do ponto de ancoragem até onde a corda começa, depois do anel, do mosquetão e do nó. */
export const COMPRIMENTO_CONEXAO_M: Readonly<Record<ConexaoDaPonta, number>> = {
  'anel-e-mosquetao': 0.34,
  'v-de-aneis': 0.34,
  mosquetao: 0.22,
  no: ALCA_DO_NO_M + 0.05,
}

/** Peças que já têm olhal ou argola: a corda clipa nelas com um mosquetão, sem anel de fita. */
const PECAS_COM_OLHAL: ReadonlySet<ObjetoTabletop['tipo']> = new Set(['pontoAncoragem', 'placaAncoragem', 'polia', 'poliaDupla', 'bloqueador', 'sistemaVM', 'descensor'])

export interface NoDaPonta {
  /** Onde a corda prende (o ponto de ancoragem). */
  readonly ponta: V
  /** Unitário da ponta para dentro da corda. */
  readonly direcao: V
  readonly conexao: ConexaoDaPonta
  /** No `v-de-aneis`: onde cada anel de fita abraça o banzo da escada (esquerdo e direito). */
  readonly apoios?: readonly [V, V]
  /** No `v-de-aneis`: a direção dos banzos (o eixo da escada), unitária. */
  readonly eixo?: V
  /** O trecho que sai desta ponta tem folga: os anéis não estão sob carga. */
  readonly frouxa?: boolean
  /**
   * Na ponta presa num mosquetão: a direção da barra que a alça do nó abraça.
   * A alça fica num plano perpendicular a ela, com a barra passando por dentro.
   */
  readonly normalDaAlca?: V
}

export interface TracadoCorda {
  /** Uma linha por pedaço contínuo (uma ponta solta, sem posição, parte a corda). */
  readonly linhas: readonly (readonly V[])[]
  readonly nos: readonly NoDaPonta[]
}

export interface OpcoesTracado {
  /**
   * Aumenta o desenho de uma peça em volta da origem dela (os equipamentos em
   * destaque): os pontos se afastam da origem nesse fator, e a roldana cresce junto.
   */
  readonly fatorDaPeca?: (objeto: ObjetoTabletop) => number
  /** Quanto crescem o anel, o mosquetão e o nó das pontas (o destaque): a corda começa mais longe. */
  readonly fatorDasConexoes?: number
}

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const unit = (a: V): V => {
  const n = comp(a)
  return n > 1e-12 ? esc(a, 1 / n) : [0, 0, 0]
}

function pontoDaPeca(cena: CenaTabletop, ref: ReferenciaPonto, opcoes: OpcoesTracado): { objeto: ObjetoTabletop; mundo: V } | null {
  const objeto = cena.objetos.find((o) => o.id === ref.idObjeto)
  if (!objeto) return null
  const ponto = pontosDeAncoragem(objeto).find((p) => p.id === ref.idPonto)
  if (!ponto) return null
  const f = opcoes.fatorDaPeca?.(objeto) ?? 1
  return { objeto, mundo: localParaMundo(esc(ponto.local, f), objeto.transformacao) }
}

/** A roldana por onde a corda passa entre os pontos `a` e `b` (entrada e saída, em qualquer ordem), ou `null`. */
function rodanaEntre(cena: CenaTabletop, a: ReferenciaPonto, b: ReferenciaPonto) {
  if (a.idObjeto !== b.idObjeto) return null
  const objeto = cena.objetos.find((o) => o.id === a.idObjeto)
  const rodana = objeto ? RODANAS[objeto.tipo]?.find((r) => (r.entrada === a.idPonto && r.saida === b.idPonto) || (r.saida === a.idPonto && r.entrada === b.idPonto)) : undefined
  return objeto && rodana ? { objeto, z: rodana.z } : null
}

/** A roldana de uma polia em que está o ponto `ref` (a entrada ou a saída dela), ou `null`. */
function rodanaDeUmPonto(objeto: ObjetoTabletop, ref: ReferenciaPonto) {
  return RODANAS[objeto.tipo]?.find((r) => r.entrada === ref.idPonto || r.saida === ref.idPonto) ?? null
}

/**
 * A corda em volta da roldana: vindo de `antes` e indo para `depois` (no
 * mundo), toca o canal nas tangentes e contorna pelo lado oposto às pernas.
 * `null` se um dos lados está dentro da roldana (não há tangente).
 */
function contornoDaRodana(objeto: ObjetoTabletop, zRodana: number, raio: number, antes: V, depois: V): V[] | null {
  const a = mundoParaLocalDoObjeto(antes, objeto.transformacao)
  const b = mundoParaLocalDoObjeto(depois, objeto.transformacao)
  const ra = Math.hypot(a[0], a[1])
  const rb = Math.hypot(b[0], b[1])
  if (ra <= raio * 1.01 || rb <= raio * 1.01) return null
  const angA = Math.atan2(a[1], a[0])
  const angB = Math.atan2(b[1], b[0])
  // o meio do contato: oposto à soma das direções das pernas
  const mx = -(a[0] / ra + b[0] / rb)
  const my = -(a[1] / ra + b[1] / rb)
  const angM = Math.hypot(mx, my) > 1e-9 ? Math.atan2(my, mx) : angA + Math.PI / 2
  const dist = (x: number, y: number) => Math.abs(Math.atan2(Math.sin(x - y), Math.cos(x - y)))
  const tangente = (ang: number, r: number) => {
    const d = Math.acos(raio / r)
    return dist(ang + d, angM) < dist(ang - d, angM) ? ang + d : ang - d
  }
  const tA = tangente(angA, ra)
  const tB = tangente(angB, rb)
  // de tA a tB passando pelo meio do contato
  let delta = Math.atan2(Math.sin(tB - tA), Math.cos(tB - tA))
  const meioCurto = tA + delta / 2
  if (dist(meioCurto, angM) > Math.PI / 2) delta = delta > 0 ? delta - 2 * Math.PI : delta + 2 * Math.PI
  const n = Math.max(6, Math.ceil(Math.abs(delta) / (Math.PI / 16)))
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = tA + (delta * i) / n
    return localParaMundo([raio * Math.cos(t), raio * Math.sin(t), zRodana], objeto.transformacao)
  })
}

/** Arredonda a dobra em `b` (entre `a` e `c`) com um arco de raio até `raio`. */
function dobraArredondada(a: V, b: V, c: V, raio: number): V[] {
  const u = unit(sub(a, b))
  const w = unit(sub(c, b))
  const cos = Math.min(1, Math.max(-1, u[0] * w[0] + u[1] * w[1] + u[2] * w[2]))
  const angulo = Math.acos(cos)
  // quase reto, ou dobrado sobre si mesmo: não há o que arredondar
  if (angulo > Math.PI - 0.02 || angulo < 0.02) return [b]
  const recuoMax = Math.min(comp(sub(a, b)), comp(sub(c, b))) * 0.4
  const recuo = Math.min(raio / Math.tan(angulo / 2), recuoMax)
  const p0 = soma(b, esc(u, recuo))
  const p1 = soma(b, esc(w, recuo))
  // Bézier quadrática com controle na quina: suave e sem sair do ângulo
  return Array.from({ length: 7 }, (_, i) => {
    const t = i / 6
    return soma(soma(esc(p0, (1 - t) * (1 - t)), esc(b, 2 * t * (1 - t))), esc(p1, t * t))
  })
}

export function tracadoDaCorda(cena: CenaTabletop, corda: CordaTabletop, opcoes: OpcoesTracado = {}): TracadoCorda {
  const refs = corda.percurso
  const pecas = refs.map((r) => pontoDaPeca(cena, r, opcoes))
  // trecho i-1 → i: a corda contorna os volumes que estiverem no caminho
  const contornos = contornosDaCorda(cena, corda, pecas.map((p) => p?.mundo ?? null))
  let sequenciaDeQuinas = 0
  const linhas: V[][] = []
  let atual: V[] = []
  /** Onde, em `atual`, começou o trecho que chega a cada ponto do percurso. */
  const inicioDoTrecho: number[] = []
  /** Pontos do meio do percurso a arredondar: índice do percurso → posição em `atual` (e em que linha). */
  const vertices = new Map<number, { linha: number; indice: number }>()

  const fechar = () => {
    if (atual.length >= 2) linhas.push(atual)
    atual = []
  }
  const ate = (destino: V, folga: number) => {
    const de = atual[atual.length - 1]!
    if (folga > 0) atual.push(...pontosDoTrecho(de, destino, folga).slice(1))
    else atual.push(destino)
  }

  for (let i = 0; i < refs.length; i += 1) {
    const p = pecas[i]
    if (!p) {
      fechar()
      continue
    }
    if (atual.length === 0) {
      inicioDoTrecho[i] = 0
      atual.push(p.mundo)
      continue
    }
    const roda = rodanaEntre(cena, refs[i - 1]!, refs[i]!)
    const antes = pecas[i - 2]?.mundo
    const depois = pecas[i + 1]?.mundo
    if (roda && antes && depois && inicioDoTrecho[i - 1] !== undefined) {
      const f = opcoes.fatorDaPeca?.(roda.objeto) ?? 1
      const contorno = contornoDaRodana(roda.objeto, roda.z * f, (FUNDO_DO_CANAL_POLIA_M + 0.006) * f, antes, depois)
      if (contorno) {
        // o trecho que chegava na entrada passa a chegar na tangente, e a corda segue pelo canal até a saída
        atual.length = inicioDoTrecho[i - 1]! + 1
        vertices.delete(i - 1)
        ate(contorno[0]!, folgaDoTrecho(corda, i - 2))
        atual.push(...contorno.slice(1))
        inicioDoTrecho[i] = atual.length - 1
        continue
      }
    }
    // a corda marcada só na entrada (ou só na saída) de uma polia também passa pela roldana
    const unica = i < refs.length - 1 ? rodanaDeUmPonto(p.objeto, refs[i]!) : null
    if (unica && depois && refs[i - 1]!.idObjeto !== refs[i]!.idObjeto && refs[i + 1]!.idObjeto !== refs[i]!.idObjeto) {
      const f = opcoes.fatorDaPeca?.(p.objeto) ?? 1
      const de = atual[atual.length - 1]!
      const contorno = contornoDaRodana(p.objeto, unica.z * f, (FUNDO_DO_CANAL_POLIA_M + 0.006) * f, de, depois)
      if (contorno) {
        inicioDoTrecho[i] = atual.length - 1
        ate(contorno[0]!, folgaDoTrecho(corda, i - 1))
        atual.push(...contorno.slice(1))
        continue
      }
    }
    inicioDoTrecho[i] = atual.length - 1
    const folgaDoTrechoAtual = folgaDoTrecho(corda, i - 1)
    // trecho frouxo pende; só o esticado vai encostado nos volumes
    if (folgaDoTrechoAtual <= 0) {
      for (const v of contornos[i - 1]?.intermediarios ?? []) {
        atual.push(v)
        vertices.set(1000 + sequenciaDeQuinas++, { linha: linhas.length, indice: atual.length - 1 })
      }
      inicioDoTrecho[i] = atual.length - 1
    }
    ate(p.mundo, folgaDoTrechoAtual)
    if (i < refs.length - 1) vertices.set(i, { linha: linhas.length, indice: atual.length - 1 })
  }
  fechar()

  // arredonda as dobras nos pontos do meio, de trás para a frente para os índices não andarem
  for (const v of [...vertices.values()].sort((a, b) => b.linha - a.linha || b.indice - a.indice)) {
    const linha = linhas[v.linha]
    if (!linha || v.indice <= 0 || v.indice >= linha.length - 1) continue
    const curva = dobraArredondada(linha[v.indice - 1]!, linha[v.indice]!, linha[v.indice + 1]!, RAIO_DOBRA_CORDA_M)
    linha.splice(v.indice, 1, ...curva)
  }

  // a ponta presa: anel de fita, mosquetão e nó, conforme o que está na ponta
  const nos: NoDaPonta[] = []
  if (corda.funcao !== 'talabarte' && corda.funcao !== 'fita') {
    const pontas: Array<[number, number, 'inicio' | 'fim']> = [
      [0, 1, 'inicio'],
      [refs.length - 1, refs.length - 2, 'fim'],
    ]
    for (const [i, vizinho, lado] of pontas) {
      const p = pecas[i]
      const q = pecas[vizinho]
      if (!p || !q || p.objeto.tipo === 'noOito' || p.objeto.tipo === 'noFiel') continue
      if (p.objeto.tipo === 'mosquetao') {
        // presa num mosquetão: a alça do nó oito abraça a barra dele voltada para a corda; as cordas
        // que terminam no mesmo mosquetão ficam lado a lado na barra
        const f = opcoes.fatorDaPeca?.(p.objeto) ?? 1
        const juntas = cordasPresasNoMosquetao(cena, p.objeto.id)
        const ordem = Math.max(0, juntas.findIndex((x) => x.id === corda.id && x.lado === lado))
        const barra = naBarra(barraDoMosquetaoParaOs(cena, p.objeto, [q.mundo], f), lugarNaBarra(ordem, juntas.length, f))
        const direcao = unit(sub(q.mundo, barra.centro))
        if (comp(direcao) === 0) continue
        const frouxa = folgaDoTrecho(corda, lado === 'inicio' ? 0 : refs.length - 2) > 0
        nos.push({ ponta: barra.centro, direcao, conexao: 'no', normalDaAlca: barra.eixo, ...(frouxa ? { frouxa } : {}) })
        const linha = lado === 'inicio' ? linhas[0] : linhas[linhas.length - 1]
        if (linha) {
          // a corda sai da barra, e não do meio do mosquetão
          if (lado === 'inicio') linha[0] = barra.centro
          else linha[linha.length - 1] = barra.centro
          aparar(linha, COMPRIMENTO_CONEXAO_M.no * (opcoes.fatorDasConexoes ?? 1), lado)
        }
        continue
      }
      const uso = pontosDeAncoragem(p.objeto).find((x) => x.id === refs[i]!.idPonto)?.uso
      if (uso !== 'ancoragem' && uso !== 'carga') continue
      const direcao = unit(sub(q.mundo, p.mundo))
      if (comp(direcao) === 0) continue
      const idPonto = refs[i]!.idPonto
      // na escada, num degrau, no topo ou no pé: um anel em cada banzo, unidos pelo mosquetão
      const naEscadaEntreBanzos = p.objeto.tipo === 'escada' && !idPonto.startsWith('banzo-')
      const conexao: ConexaoDaPonta =
        p.objeto.tipo === 'envolvimento'
          ? 'no'
          : uso === 'carga' || PECAS_COM_OLHAL.has(p.objeto.tipo)
            ? 'mosquetao'
            : naEscadaEntreBanzos
              ? 'v-de-aneis'
              : 'anel-e-mosquetao'
      const frouxa = folgaDoTrecho(corda, lado === 'inicio' ? 0 : refs.length - 2) > 0
      if (conexao === 'v-de-aneis') {
        const pontos = pontosDeAncoragem(p.objeto)
        const local = pontos.find((x) => x.id === idPonto)!.local
        const f = opcoes.fatorDaPeca?.(p.objeto) ?? 1
        const [esq, dir] = banzosDoPontoDaEscada(local)
        const noMundo = (l: V) => localParaMundo(esc(l, f), p.objeto.transformacao)
        nos.push({
          ponta: p.mundo,
          direcao,
          conexao,
          apoios: [noMundo(esq), noMundo(dir)],
          eixo: unit(sub(noMundo(pontos.find((x) => x.id === 'topo')!.local), noMundo(pontos.find((x) => x.id === 'pe')!.local))),
          ...(frouxa ? { frouxa } : {}),
        })
      } else {
        nos.push({ ponta: p.mundo, direcao, conexao, ...(frouxa ? { frouxa } : {}) })
      }
      // a corda começa depois da conexão (se couber)
      const linha = lado === 'inicio' ? linhas[0] : linhas[linhas.length - 1]
      if (linha) aparar(linha, COMPRIMENTO_CONEXAO_M[conexao] * (opcoes.fatorDasConexoes ?? 1), lado)
    }
  }
  return { linhas, nos }
}

/** As pontas de corda presas no mosquetão, na ordem das cordas: cada uma ganha o seu lugar na barra. */
function cordasPresasNoMosquetao(cena: CenaTabletop, idMosquetao: string): Array<{ id: string; lado: 'inicio' | 'fim' }> {
  const pontas: Array<{ id: string; lado: 'inicio' | 'fim' }> = []
  for (const c of cena.cordas ?? []) {
    if (c.funcao === 'fita' || c.funcao === 'talabarte' || c.percurso.length < 2) continue
    if (c.percurso[0]!.idObjeto === idMosquetao) pontas.push({ id: c.id, lado: 'inicio' })
    if (c.percurso[c.percurso.length - 1]!.idObjeto === idMosquetao) pontas.push({ id: c.id, lado: 'fim' })
  }
  return pontas
}

/** Tira `distancia` metros de uma ponta da linha (no lugar), se a linha tiver bem mais que isso. */
function aparar(linha: V[], distancia: number, lado: 'inicio' | 'fim'): void {
  let total = 0
  for (let k = 1; k < linha.length; k += 1) total += comp(sub(linha[k]!, linha[k - 1]!))
  if (total < distancia * 2 + 0.1) return
  if (lado === 'fim') linha.reverse()
  let falta = distancia
  while (linha.length >= 2) {
    const d = comp(sub(linha[1]!, linha[0]!))
    if (d > falta) {
      linha[0] = soma(linha[0]!, esc(unit(sub(linha[1]!, linha[0]!)), falta))
      break
    }
    falta -= d
    linha.shift()
  }
  if (lado === 'fim') linha.reverse()
}
